import type { AbstractPlayableContent, TemporalIndicators } from "../playable/models/AbstractPlayableContent";
import { Division } from "../playable/models/Division";
import * as Tone from "tone";
import { Platform } from "quasar";
import { synthController } from "./SynthController";
import { TinyEmitter } from "tiny-emitter";
import Vue from "vue";
import { playerConfig } from "../player/models/PlayerConfig";
import * as Audio from "../audio"
import { type AudioPlayerState, type AudioTimelinePartEvent, type AudioTimelinePartEventVoices, type MetaTimelinePartEvent, type TimelinePartEvents, type TimelinePartEvent, defaultAudioPlayerState } from "./models/AudioPlayerTypes";
import { RhythmicBloc } from "../playable/models/RhythmicBloc";
import { analyticsController } from "../user/analytics/AnalyticsController";
import {playerModals} from "src/modules/player/models/PlayerModals";
import { NoteStatus } from "../playable/models/Note";
import { KEYS_TO_NOTES } from "src/modules/musical-components/keyboard/models/Keyboard";

const MinuteInSeconds = 60
const SecondInMiliseconds = 1000
const LookAHead = 0.2
export const LookAHeadInMilliseconds = LookAHead * SecondInMiliseconds

// Use Tone.getTransport() as a base
class AudioPlayer extends TinyEmitter {

  // TODO: could be something simpler to do with that :
  // The transport emits the events: “start”, “stop”, “pause”, and “loop” which are called with the time of that event as the argument.
  public readonly state = Vue.observable<AudioPlayerState>({ ...defaultAudioPlayerState })

  private tonePart: Tone.Part | null = null;
  private playableContent: AbstractPlayableContent | null = null;
  private divisions: Record<number, Division> | null = null
  private countdownValue = 4
  private countdownEvent: TimelinePartEvent | null = null
  private divisionOffset = 0
  private ticksPerDivision = 96         // PPQ / divisionPerBeat — constant per song, cached after setupTimeSignature
  private metronomeScheduleId: number | null = null  // Tone.Transport scheduleRepeat id for the metronome

  public isFirstCountdownTick = true

  get countdown() {
    return this.countdownValue
  }

  constructor() {
    super();

    // For some reason need to use lambda here to keep context from some views (eg: PlayerTimerController). should investigate
    this.on('play', (withCountdown: boolean = true) => this.play(withCountdown));
    this.on('pause', () => this.pause());
    this.on('stop', () => this.stop());

    this.on('setTimer', (time: number, snap: boolean = false, withOffset = true) => this.setTimer(time, snap, withOffset));
    this.on('setTimerToLoopStart', () => this.setTimerToLoopStart());
    this.on('loopRestart', (delay: number = this.RESTART_DELAY_MS) => this.restartFromLoop(delay));

    this.on('setCurrentDivision', (division: Division | null) => {
      if ((this.state.playerStatus === 'pause' || this.state.playerStatus === 'player_controlled') && division != null) {
        this.state.currentDivision = Division.divisionAsNextHandprintDivisionForPiano(division)
      }
      else if (this.state.playerStatus === 'countdown' && this.divisions && this.countdownEvent) {
        this.state.currentDivision = Division.divisionAsNextHandprintDivisionForPiano(this.divisions[this.countdownEvent.divisionId])
      }
      else {
        this.state.currentDivision = division
      }

      this.emit('onDivisionChange', this.state.currentDivision)
    })

    this.on('setTempoFromSpeed', (value) => this.setTempoFromSpeed(value))

    // Quite a specific one, only called for sound notification in case restart should be automatic
    // Work only on top of delayedstart when loopRestart has been called
    this.on('increaseLoopRestartDelayIfNeeded', (delay: number = this.RESTART_DELAY_MS) => this.increaseLoopRestartDelayIfNeeded(delay));

    this.on('clearTimeouts', () => this.clearTimeouts())
  }

  /* New Play */

  public initPlayableContent(playableContent: AbstractPlayableContent, _divisions: Division[], withCountdownDivisions = true) {
    try {
      this.state.isLoading = true
      this.resetPlayableContent();
      synthController.initSynth();

      this.setupTimeSignature(playableContent.temporalIndicators);
      this.initTempo(playableContent.temporalIndicators.tempo, playerConfig.state.speed);
      Tone.getTransport().seconds = 0
      this.setCountdown(playableContent.temporalIndicators.beatPerMeasure)
      this.playableContent = playableContent;
      const divisions = this.computeDivisions(_divisions, playableContent, withCountdownDivisions)
      const partArray = this.computePartArray(divisions, playableContent);
      this.tonePart = new Tone.Part((time, division) => this.playPartDivision(time, division), partArray).start(0);
      Tone.getContext().lookAhead = LookAHead;
      this.setupMetronomeSchedule();
    }
    catch (error) {
      this.triggerError(error, 'initPlayableContent()')
    }
    finally {
      this.state.isLoading = false
    }
  }
  private setupTimeSignature(temporalIndicators: TemporalIndicators) {
    this.state.divisionPerBeat = temporalIndicators.divisionPerBeat;
    this.state.beatPerMeasure = temporalIndicators.beatPerMeasure;
    const isTernary = temporalIndicators.divisionPerBeat === 3

    const unit = isTernary ? 8 : 4
    const unitCount = isTernary ? (temporalIndicators.beatPerMeasure * 3) : temporalIndicators.beatPerMeasure;

    Tone.getTransport().timeSignature = [unitCount, unit];
    // PPQ = divisionPerBeat * 96 → ticksPerDivision = PPQ / divisionPerBeat = 96 (constant for any time signature)
    Tone.getTransport().PPQ = temporalIndicators.divisionPerBeat * 96;
    this.ticksPerDivision = Tone.getTransport().PPQ / temporalIndicators.divisionPerBeat;
  }

  /**
   * TODO: could simplify this function
   *       For audio
   *          by precomputing objects like synthKeys
   *          by having soundParameters in local AudioPlayer instead of vuex store and without any compute for volume
   *          by having only one event by division ?  (not sure it's really useful)
   *          by having metronome not working has event but has schedule repeat in Tone.JS
   *
   * @param time
   * @param division
   * @private
   */
  private playPartDivision(time: number, division: TimelinePartEvents) {
    try {
      // Read Vue reactive state once per division call — avoids per-event proxy overhead
      const soundParameters = playerConfig.state.soundParameters;
      const isMicReady = Audio.musicTranscriptionController.state.workerState === 'ready';
      const draw = Tone.getDraw();

      division.events.forEach((event) => {
        if (event.type === 'audio' && this.state.playerStatus !== 'countdown') {
          if (soundParameters.actives.MAIN && !isMicReady) {
            const audioEvent = event as AudioTimelinePartEvent;
            if (audioEvent.voice === 'melody' && soundParameters.actives.MELODY) {
              synthController.playMelodyPrecomputed(audioEvent.noteNames, audioEvent.durations, soundParameters.volumes.MELODY / 100, time);
            }
            else if (audioEvent.voice === 'piano_left' && soundParameters.actives.LEFT) {
              synthController.playPianoPrecomputed(audioEvent.noteNames, audioEvent.durations, soundParameters.volumes.LEFT / 100, time);
            }
            else if (audioEvent.voice === 'piano_right' && soundParameters.actives.RIGHT) {
              synthController.playPianoPrecomputed(audioEvent.noteNames, audioEvent.durations, soundParameters.volumes.RIGHT / 100, time);
            }
          }
        }
        else if (event.type === 'low_rate_refresh') {
          draw.schedule(() => {
            this.emit('onTimerChangeLowRate', event.divisionId)
          }, time);
        }
        else if (event.type === 'high_rate_refresh') {
          draw.schedule(() => {
            this.state.timer = event.divisionId;
            this.emit('setCurrentDivision', this.divisions?.[event.divisionId])
            this.emit('onTimerChangeHighRate', event.divisionId)
          }, time);
        }
        else if (event.type === 'meta' && event.metaEvent === 'end') {
          const loopParameters = playerConfig.state.loopParameters
          if (loopParameters.isActive) {
            if ((event.divisionId >= (loopParameters.playableContent.temporalIndicators.endDivision - 1)) && ((loopParameters.playableContent.playableType === event.playableType && event.playableType !== 'list') || (loopParameters.playableContent.playableType === 'list' && event.playableType === 'part'))) {
              Tone.getTransport().pause(time + 0.01);
              draw.schedule(() => {
                setTimeout(() => {
                  this.emit('pause', true, division.divisionId);
                  this.emit('loopEnd');
                }, this.state.divisionPerBeat * this.divisionDuration)
              }, time)
            }
          }
          else if (event.divisionId >= (this.playableContent!.temporalIndicators.endDivision - 1)) {
            Tone.getTransport().pause(time + 0.01);
            draw.schedule(() => {
              setTimeout(() => {
                this.emit('pause', false, division.divisionId);
                this.emit('setTimer', this.playableContent!.temporalIndicators.startDivision, true, false);
              }, this.state.divisionPerBeat * this.divisionDuration)
            }, time)
          }
        }
        else if (event.type === 'countdown_stop') {
          this.state.playerStatus = 'play'
          this.cancelCountdown()
        }
      })
    }
    catch (error) {
      this.triggerError(error, 'playPartDivision()')
    }
  }

  private setupMetronomeSchedule() {
    if (this.metronomeScheduleId !== null) {
      Tone.getTransport().clear(this.metronomeScheduleId);
    }
    const beatPeriod = `${this.state.divisionPerBeat * this.ticksPerDivision}i`;
    this.metronomeScheduleId = Tone.getTransport().scheduleRepeat((time) => {
      const sp = playerConfig.state.soundParameters;
      if (sp.actives.METRONOME || this.state.playerStatus === 'countdown') {
        synthController.playMetronome(time, sp.volumes.METRONOME);
      }
      if (this.state.playerStatus === 'countdown' && !this.isFirstCountdownTick) {
        this.setCountdown(this.countdown - 1);
      } else {
        this.isFirstCountdownTick = false;
      }
    }, beatPeriod, 0);
  }

  public resetPlayableContent() {
    this.stop();
    if (this.metronomeScheduleId !== null) {
      Tone.getTransport().clear(this.metronomeScheduleId);
      this.metronomeScheduleId = null;
    }
    this.tonePart?.dispose();
    this.tonePart = null
    this.state.baseTempo = defaultAudioPlayerState.baseTempo
    this.state.beatPerMeasure = defaultAudioPlayerState.beatPerMeasure
    this.state.currentDivision = defaultAudioPlayerState.currentDivision
    this.state.divisionDuration = defaultAudioPlayerState.divisionDuration
    this.state.divisionPerBeat = defaultAudioPlayerState.divisionPerBeat
    this.state.playerStatus = defaultAudioPlayerState.playerStatus
    this.state.tempo = defaultAudioPlayerState.tempo
    this.state.timer = defaultAudioPlayerState.timer
    this.playableContent = null;
    this.divisions = null
    this.countdownValue = 4
    this.countdownEvent = null
    this.divisionOffset = 0
    this.state.error = false
  }

  // TODO: handle pick up measures (should be embed in division ?)
  /**
   * low_rate_refresh -> Event to send main thread update that could be done less frequently than each division. Could be good for intensive tasks
   * high_rate_refresh -> Event to send main thread update that should be done each division.
   *
   * Following audio events could be condensed in one event if we need to simplify it (if it helps in terms of performance)
   * metronome -> Event to play metronome sound
   * audio -> Event to play synth (melody, piano left, piano right)
   *
   * meta -> More global and ponctual event that could trigger anything (like start, end)
   *
   * @param divisions
   * @param playableContent
   * @private
   */
  private computePartArray(divisions: Division[], playableContent: AbstractPlayableContent): TimelinePartEvents[] {
    const parts: TimelinePartEvents[] = [];
    const metaEvents = this.computeMetaEventsForTimeline(playableContent);

    if (playableContent instanceof RhythmicBloc) {
      metaEvents.push({
        divisionId: divisions[divisions.length - 1].id,
        metaEvent: 'end',
        playableType: 'bloc',
        type: 'meta'
      })
    }

    // Build a Map so meta-event lookup is O(1) per division instead of O(n) linear scan
    const metaByDivision = new Map<number, MetaTimelinePartEvent[]>();
    for (const e of metaEvents) {
      let arr = metaByDivision.get(e.divisionId);
      if (!arr) { arr = []; metaByDivision.set(e.divisionId, arr); }
      arr.push(e);
    }

    const lowRateRefreshRate = playableContent.temporalIndicators.divisionPerBeat * playableContent.temporalIndicators.beatPerMeasure;

    divisions.forEach((division) => {
      const divisionEvents: TimelinePartEvent[] = [];

      if (!this.state.currentDivision && division.id >= 0) {
        this.emit('setCurrentDivision', division)
      }

      if (division.id % lowRateRefreshRate === 0) {
        divisionEvents.push({ divisionId: division.id, type: 'low_rate_refresh' });
      }
      divisionEvents.push({ divisionId: division.id, type: 'high_rate_refresh' });

      // Metronome is handled by setupMetronomeSchedule() via scheduleRepeat — not emitted per-division

      if (division.melodyNote && (division.melodyNote.noteStatus === NoteStatus.DETACHED || division.melodyNote.noteStatus === NoteStatus.ON)) {
        const melodyNote = division.melodyNote;
        if ((melodyNote.noteStatus === NoteStatus.DETACHED || melodyNote.noteStatus === NoteStatus.ON) && melodyNote.duration > 0) {
          divisionEvents.push({
            divisionId: division.id,
            type: 'audio',
            key: [melodyNote.key],
            noteNames: [KEYS_TO_NOTES[melodyNote.key]],
            durations: [this.computeTime(melodyNote.duration)],
            voice: 'melody',
          });
        }
      }

      for (const hand of ['LEFT', 'RIGHT'] as const) {
        const notes = division.currentHandprint.filter(
          (note) => note.hand === hand && (note.noteStatus === NoteStatus.DETACHED || note.noteStatus === NoteStatus.ON) && note.duration > 0
        );
        if (notes.length > 0) {
          const keys = notes.map((note) => note.key);
          divisionEvents.push({
            divisionId: division.id,
            type: 'audio',
            key: keys,
            noteNames: keys.map((k) => KEYS_TO_NOTES[k]),
            durations: notes.map((note) => this.computeTime(note.duration)),
            voice: `piano_${hand.toLowerCase()}` as AudioTimelinePartEventVoices,
          });
        }
      }

      // O(1) meta event lookup via pre-built Map
      const divisionMetaEvents = metaByDivision.get(division.id);
      if (divisionMetaEvents) divisionEvents.push(...divisionMetaEvents);

      parts.push({
        divisionId: division.id,
        events: divisionEvents,
        time: this.computeTime(division.id + this.divisionOffset),
      });
    });

    return parts;
  }
  private computeMetaEventsForTimeline(playableContent: AbstractPlayableContent, parentStartDivision = 0): MetaTimelinePartEvent[] {
    const metaEvents: MetaTimelinePartEvent[] = [];
    const actualStartDivision = parentStartDivision + playableContent.temporalIndicators.startDivision;
    const actualEndDivision = parentStartDivision + playableContent.temporalIndicators.endDivision - 1

    if (playableContent.playableType === 'version') {
      metaEvents.push({
        divisionId: actualStartDivision,
        type: 'meta',
        metaEvent: 'start',
        playableType: 'list',
      });

      metaEvents.push({
        divisionId: actualEndDivision,
        type: 'meta',
        metaEvent: 'end',
        playableType: 'list',
      });
    }

    metaEvents.push({
      divisionId: actualStartDivision,
      type: 'meta',
      metaEvent: 'start',
      playableType: playableContent.playableType,
    });
    if (playableContent.playableTimeline.length > 0) {
      playableContent.playableTimeline.forEach((childPlayableContent) => {
        metaEvents.push(...this.computeMetaEventsForTimeline(childPlayableContent, actualStartDivision));
      })
    }
    metaEvents.push({
      divisionId: actualEndDivision,
      type: 'meta',
      metaEvent: 'end',
      playableType: playableContent.playableType,
    });

    return metaEvents;
  }

  // Returns the Tone.js tick string for a given division ID.
  // ticksPerDivision is always PPQ/divisionPerBeat = 96 (cached in setupTimeSignature).
  private computeTime(divisionId: number) {
    return `${divisionId * this.ticksPerDivision}i`;
  }

  /* Main Timer Function */

  get timerFlat() {
    return Math.max(this.state.timer, 0);
  }

  private computeDivisions(_divisions: Division[], playableContent: AbstractPlayableContent, withCountdownDivisions = true) {
    let divisions: Division[] = []
    if (withCountdownDivisions) {
      this.divisionOffset = playableContent.temporalIndicators.divisionPerBeat * playableContent.temporalIndicators.beatPerMeasure;
      const countdownDivisions: Division[] = []
      for (let index = 0; index < this.divisionOffset; index++) {
        const division = new Division()
        division.id = -(index + 1)
        countdownDivisions.push(division)
      }

      divisions = [...countdownDivisions.reverse(), ..._divisions]
    }
    else {
      this.divisionOffset = 0
      divisions = _divisions
    }

    this.divisions = divisions.reduce((acc, division) => {
      acc[division.id] = division
      return acc
    }, {} as Record<number, Division>)

    return divisions
  }

  private cancelCountdown() {
    this.isFirstCountdownTick = true
    this.setCountdown(this.playableContent?.temporalIndicators.beatPerMeasure || 4)
    if (this.countdownEvent) {
      const time = this.computeTime(this.countdownEvent.divisionId + this.divisionOffset)
      const timelinePartEvents = this.tonePart?.at(time) as Tone.ToneEvent<TimelinePartEvents>
      if (timelinePartEvents) {
        timelinePartEvents.value.events = timelinePartEvents.value?.events?.filter((el) => el.type !== 'countdown_stop') || []
        this.tonePart?.at(time, timelinePartEvents.value)
        this.countdownEvent = null
      }
    }
  }

  // Set from App.vue (iOS only) to the component's rebuildAudioContext(): a full close + new
  // AudioContext + resynth + content-reschedule. play() falls back to this when a bare resume()
  // isn't enough — see isContextTrulyRunning()'s comment for why resume() alone can't be trusted.
  public requestContextRebuild: (() => Promise<void>) | null = null;

  // A WebKit "zombie" context can report state === 'running' right after resume() without the
  // native audio unit actually having reconnected (see rebuildAudioContext()'s comment in App.vue).
  // .state alone can't tell a live context from a zombie one, but currentTime can: it only
  // advances while audio is genuinely being processed, so a wait-and-compare is a real liveness
  // check instead of trusting a self-reported flag. The wait is deliberately generous (not just
  // a couple render quanta) — field reports of this exact technique converge on sampling over
  // roughly 500ms-2s, since a shorter window risks reading a device that's merely slow to settle
  // (e.g. right after a cold launch) as truly dead, which is the false-positive this whole
  // mechanism exists to avoid in the first place.
  private async isContextTrulyRunning(): Promise<boolean> {
    const rawContext = Tone.getContext().rawContext as AudioContext;
    if (rawContext.state !== 'running') {
      return false;
    }
    const startTime = rawContext.currentTime;
    await new Promise((resolve) => setTimeout(resolve, 600));
    return rawContext.state === 'running' && rawContext.currentTime > startTime;
  }

  // Polls state.isLoading down rather than trusting requestContextRebuild()'s own promise to mean
  // "fully ready" — see its call site for why those aren't the same thing. Bounded so a failed
  // reload (e.g. the network fetch behind it rejects) can't hang play() forever; the subsequent
  // tonePart/liveness check still catches that case and reports it as a real failure.
  private async waitUntilContentReloaded(timeoutMs = 8000): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while (this.state.isLoading && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  // Makes sure the AudioContext is genuinely producing audio before we commit to a countdown,
  // instead of optimistically starting one and only discovering it's dead once it ends (which is
  // what used to leave the countdown visibly "stuck"). The zombie-context problem this guards
  // against (resume() resolving, .state even reporting 'running', without audio actually working)
  // is iOS/WebKit-specific — other platforms keep the previous plain-resume behaviour untouched,
  // since there's no known equivalent failure mode there to guard against, and no reason to risk a
  // new false-positive abort on playback that already works correctly today.
  private async ensurePlaybackContextIsReady(): Promise<boolean> {
    if (!Platform.is.ios) {
      if (Tone.getContext().state !== 'running') {
        await Tone.getContext().resume();
      }
      return true;
    }

    // isContextTrulyRunning() is deliberately slow (it has to be, to be trustworthy — see its own
    // comment) — paying that cost on every single healthy Play tap would make the common case
    // feel sluggish for no reason. contextWasInterrupted (set live by watchContextInterruption's
    // statechange listener) is the signal that something has actually gone wrong since the
    // context was last known-good; when it's unset and .state already says 'running', trust that
    // plain, instant read exactly like before this fix existed.
    if (!this.contextWasInterrupted && Tone.getContext().state === 'running') {
      return true;
    }

    if (await this.isContextTrulyRunning()) {
      this.contextWasInterrupted = false;
      return true;
    }

    if (Tone.getContext().state !== 'running') {
      await Tone.getContext().resume().catch(() => undefined);
    }
    if (await this.isContextTrulyRunning()) {
      this.contextWasInterrupted = false;
      return true;
    }

    // A plain resume() on an already-"running"-but-dead context is commonly a no-op — cycling it
    // through suspend() first forces WebKit to actually re-engage before resuming, which several
    // real-world reports found more reliable than resume() alone for this specific zombie case,
    // and it's far cheaper than a full rebuild if it works.
    await Tone.getContext().suspend().catch(() => undefined);
    await Tone.getContext().resume().catch(() => undefined);
    if (await this.isContextTrulyRunning()) {
      this.contextWasInterrupted = false;
      return true;
    }

    if (this.requestContextRebuild) {
      await this.requestContextRebuild().catch(() => undefined);
      // requestContextRebuild() only waits for the context + synth samplers to come back — the
      // song's tonePart is rescheduled asynchronously afterward, once App.vue's
      // 'reloadPlayableContent' event reaches the player page and its own network fetch
      // completes. state.isLoading spans that whole tail (set at the start of the rebuild, only
      // cleared by initPlayableContent() once tonePart is actually rebuilt), so wait for it
      // before trusting tonePart is usable again — otherwise playWithCountDown() would silently
      // schedule against a still-stale tonePart.
      await this.waitUntilContentReloaded();
      if (this.tonePart && await this.isContextTrulyRunning()) {
        this.contextWasInterrupted = false;
        return true;
      }
    }

    this.triggerError(
      new Error(`AudioContext could not be made ready for playback (state: ${Tone.getContext().state})`),
      'ensurePlaybackContextIsReady()',
    );
    return false;
  }

  private async play(withCountdown = true) {
    const blockingModals = playerModals.state.activeModals.filter(m => m !== 'teacherConv');
    if (blockingModals.length > 0) {
      return
    }
    try {
      // Commit any in-flight scroll/flick settle now, synchronously, so the timeline component's
      // pending 'moved-end'/inertia-end handling can't land after playback started and interfere with it.
      this.emit('forceSettleScroll');
      this.clearTimeouts();

      if (!(await this.ensurePlaybackContextIsReady())) {
        return;
      }

      if (this.tonePart?.state === 'stopped') {
        Tone.getTransport().seconds = 0;
        this.tonePart.start()
      }

      if (!localStorage.getItem("disableCountdown") && withCountdown) {
        this.playWithCountDown();
      }
      else {
        Tone.getTransport().start();
        this.state.playerStatus = 'play';
      }
    } catch (error) {
      this.triggerError(error, 'play()')
    }
  }

  private startCountdownTimeout: NodeJS.Timeout
  private transcriptionSyncTimeout: NodeJS.Timeout
  private playWithCountDown() {
    try {
      this.state.playerStatus = 'countdown';
      const divisionCountdownEnded = this.state.timer
      const divisionCountdownStarted = divisionCountdownEnded - (this.state.divisionPerBeat * this.state.beatPerMeasure)
      this.countdownEvent = {
        divisionId: divisionCountdownEnded,
        type: 'countdown_stop'
      }
      const time = this.computeTime(divisionCountdownEnded + this.divisionOffset)
      const timelinePartEvents = this.tonePart?.at(time) as Tone.ToneEvent<TimelinePartEvents>
      if (timelinePartEvents) {
        if (!timelinePartEvents.value || !Array.isArray(timelinePartEvents.value.events)) {
          timelinePartEvents.value.events = []
        }
        timelinePartEvents.value.events.unshift(this.countdownEvent)
        this.tonePart?.at(time, timelinePartEvents.value)

        this.emit('setTimer', divisionCountdownStarted);

        this.startCountdownTimeout = setTimeout(() => {
          if (Tone.getContext().state !== 'running') {
            // The context died somewhere in the countdown window (e.g. iOS reclaimed the audio
            // session for another app) — surface the existing error banner instead of silently
            // starting a Transport that won't produce any sound.
            this.cancelCountdown();
            this.state.playerStatus = 'pause';
            this.triggerError(
              new Error(`AudioContext not running at countdown end (state: ${Tone.getContext().state})`),
              'playWithCountDown() deferred start',
            );
            return;
          }
          Tone.getTransport().start();
        }, this.divisionDuration * 2)

        this.transcriptionSyncTimeout = setTimeout(() => { // Set a timeout to compute first division timing to sync audio recording
          Audio.musicTranscriptionTimer.computeDivisionStartAt();
        }, this.state.divisionPerBeat * this.state.beatPerMeasure * this.state.divisionDuration)
      }
      else if (
        playerConfig.state.loopParameters.playableContent.playableType !== 'list' &&
        this.state.timer >= (playerConfig.state.loopParameters.playableContent.temporalIndicators.endDivision ?? this.playableContent?.temporalIndicators.endDivision)
      ) {
        this.state.playerStatus = 'pause'
        this.setTimerToLoopStart()
        this.restartFromLoop()
      }
    }
    catch (error) {
      this.triggerError(error, 'playWithCountDown()')
    }
  }

  private pause() {
    Tone.getTransport().pause();
    this.clearTimeouts();
    this.isFirstCountdownTick = true

    if (this.state.playerStatus === 'countdown') {
      this.emit('setTimer', this.countdownEvent?.divisionId, true, false);
      this.cancelCountdown()
    }

    this.state.playerStatus = 'pause';
  }
  private stop() {
    Tone.getTransport().stop();
    this.clearTimeouts();
    this.cancelCountdown();

    this.state.playerStatus = 'pause';
    this.setTimer(0);
  }
  public stopPart() {
    if (this.tonePart?.state === 'started') {
      this.tonePart?.stop()
    }
  }

  private setTimer(time: number, snap: boolean = false, withOffset = true) {
    let actualTime = time;
    if (snap) {
      actualTime = Math.floor(time / this.state.divisionPerBeat) * this.state.divisionPerBeat;
    }

    // console.log('setTimer : ' + time + ' -> ' + actualTime + ' (snap : ' + snap + ')');
    Tone.getTransport().position = this.computeTime(actualTime + (withOffset ? this.divisionOffset : 0));
    this.state.timer = actualTime;
    this.emit('onTimerChangeLowRate', actualTime, withOffset)
    this.emit('onTimerChangeHighRate', actualTime, withOffset)
    this.emit('setCurrentDivision', this.divisions?.[actualTime])
  }
  setTimerToLoopStart() {
    this.emit('setTimer', playerConfig.state.loopParameters.playableContent.temporalIndicators.startDivision, true, false);
  }
  private setCountdown(value: number) {
    this.countdownValue = value
    this.emit('countdown', this.countdownValue)
  }

  private delayBeforeStartTimeout: any = -1;
  private delayBeforeStartDuration: number = 0;
  readonly RESTART_DELAY_MS = 1000;
  private restartFromLoop(delay = this.RESTART_DELAY_MS) {
    this.pause();

    this.state.playerStatus = 'player_controlled';

    // Added timeout to prevent lookahead
    setTimeout(() => {
      this.emit('setTimer', playerConfig.state.loopParameters.playableContent.temporalIndicators.startDivision, true, false);
    }, LookAHeadInMilliseconds);

    // Delay start
    this.delayBeforeStartDuration = delay;
    this.delayBeforeStartTimeout = setTimeout(() => {
      this.delayBeforeStartTimeout = 0;
      this.play();
    }, delay);
  }

  increaseLoopRestartDelayIfNeeded(delay: number) {
    if (this.delayBeforeStartDuration > 0 && this.delayBeforeStartDuration < delay) {
      this.clearTimeouts();
      this.state.playerStatus = 'pause';
      this.delayBeforeStartTimeout = setTimeout(() => {
        this.delayBeforeStartTimeout = 0;
        if (playerConfig.state.playerView === 'piano' || playerConfig.state.playerView === 'partition') {
          this.play()
        }
      }, delay);
    }
  }

  private clearTimeouts() {
    clearTimeout(this.delayBeforeStartTimeout);
    clearTimeout(this.startCountdownTimeout);
    clearTimeout(this.transcriptionSyncTimeout);
    // Reset so increaseLoopRestartDelayIfNeeded cannot schedule a stale play()
    // after the user has manually paused (the restart cycle is over).
    this.delayBeforeStartDuration = 0;
  }

  /* Speed & Time Signature */

  get baseTempo() {
    return this.state.baseTempo
  }
  get tempo() {
    return this.state.tempo
  }

  private initTempo(tempo: number, speed: number) {
    this.state.baseTempo = tempo;
    this.setTempoFromSpeed(speed);
  }
  public changeTempoUsingRatio(ratio: number) {
    this.setTempo(this.baseTempo * ratio);
  }
  public setTempo(tempo: number, withSetSpeed = true) {
    Tone.getTransport().bpm.value = tempo;
    this.state.tempo = tempo;
    this.computeDurations(withSetSpeed)
  }

  get divisionDuration() {
    return this.state.divisionDuration
  }
  get divisionDurationInSeconds() {
    return this.divisionDuration / SecondInMiliseconds
  }

  private setTempoFromSpeed(value: number) {
    this.setTempo(value * this.baseTempo, false);
  }
  private computeDurations(withSetSpeed = true) {
    if (withSetSpeed) {
      playerConfig.setSpeed(this.tempo / this.baseTempo, false)
    }
    const beatDuration = SecondInMiliseconds / ((playerConfig.state.speed * this.baseTempo) / MinuteInSeconds)
    this.state.divisionDuration = beatDuration / this.state.divisionPerBeat
  }

  public triggerError(error: any, method: string) {
    analyticsController.logBasicAnalyticsEvent('audio player error on play part', 'player', {
      error: error.message,
      method
    })

    Vue.prototype.Monitoring?.captureException(error)

    this.state.error = true
    this.state.isLoading = false
  }

  private contextInterruptionCleanup: (() => void) | null = null;
  // Set whenever the watcher below actually observes the context go non-running. This is the
  // only reliable record that an interruption happened: by the time the app is foregrounded again
  // and App.vue re-checks Tone.getContext().state, WebKit will often already be reporting
  // 'running' again on a context that never really reconnected (see rebuildAudioContext()'s
  // comment) — so that later state read can't be trusted to decide whether a rebuild is needed.
  // Consumed (and reset) by App.vue.onVisibilityChange() via consumeContextInterruptionFlag().
  private contextWasInterrupted = false;

  // Watches the given AudioContext for state changes so a genuine interruption mid-playback (e.g.
  // iOS reclaiming the audio session for another app — YouTube, Spotify, a phone call, etc.) stops
  // the player immediately and surfaces the existing error banner, instead of leaving a dead
  // Transport silently ticking with no sound. Call again after every AudioContext replacement (see
  // App.vue's iOS context-rebuild handling) so the watcher always targets the live context.
  public watchContextInterruption(rawContext: AudioContext) {
    this.contextInterruptionCleanup?.();

    const handler = () => {
      if (rawContext.state === 'running') {
        return;
      }

      this.contextWasInterrupted = true;

      if (this.state.playerStatus === 'play' || this.state.playerStatus === 'countdown') {
        const interruptedDuring = this.state.playerStatus;
        this.clearTimeouts();
        this.cancelCountdown();
        this.state.playerStatus = 'pause';
        this.triggerError(
          new Error(`AudioContext interrupted during ${interruptedDuring} (state: ${rawContext.state})`),
          'onContextStateChange()',
        );
      }
    };

    rawContext.addEventListener('statechange', handler);
    this.contextInterruptionCleanup = () => rawContext.removeEventListener('statechange', handler);
  }

  // Consumed by App.vue.onVisibilityChange() to decide whether to rebuild the AudioContext on
  // foreground, instead of re-reading .state at that point (see contextWasInterrupted above for
  // why that read can't be trusted).
  public consumeContextInterruptionFlag(): boolean {
    const wasInterrupted = this.contextWasInterrupted;
    this.contextWasInterrupted = false;
    return wasInterrupted;
  }
}

const audioPlayer = new AudioPlayer();

export default audioPlayer;
